import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check with error handling for invalid refresh tokens
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      // Handle refresh token errors gracefully
      if (error && error?.message?.includes('refresh')) {
        console.warn('Session refresh failed, clearing invalid session:', error?.message)
        // Clear the invalid session from storage
        supabase?.auth?.signOut({ scope: 'local' })
        authStateHandlers?.onChange(null, null)
        return
      }
      authStateHandlers?.onChange(null, session)
    })?.catch((err) => {
      console.error('Error checking session:', err)
      // Clear any corrupted session data
      supabase?.auth?.signOut({ scope: 'local' })
      authStateHandlers?.onChange(null, null)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        // Handle TOKEN_REFRESHED event errors
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('Token refresh failed, clearing session')
          supabase?.auth?.signOut({ scope: 'local' })
        }
        authStateHandlers?.onChange(event, session)
      }
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      if (error) {
        console.error('[AuthContext] Sign in error:', error)
      }
      return { data, error }
    } catch (error) {
      console.error('[AuthContext] Unexpected sign in error:', error)
      return { error: { message: error?.message || 'Network error. Please try again.' } }
    }
  }

  // Add signup method with profile data support
  const signUp = async (email, password, fullName = '', avatarUrl = '') => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            avatar_url: avatarUrl,
            role: 'user'
          }
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Add Google OAuth sign-in method
  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `https://trainingload.hubsportonline.co.uk/auth/callback`
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Add password reset method - Uses token-safe flow to prevent email scanner consumption
  // Flow: Email → /secure-reset?token=TOKENHASH&redirect_to=REDIRECT → User clicks button → 
  //       Supabase verify → /reset-password with access_token in hash
  // This prevents corporate email scanners from consuming the token
  const resetPassword = async (email) => {
    try {
      const origin = window.location?.origin;
      // Redirect to secure-reset page first (token-safe)
      // The secure-reset page will then redirect to Supabase verify, which redirects to reset-password
      const secureResetUrl = `${origin}/secure-reset`;
      const finalRedirectUrl = `${origin}/reset-password`;
      
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${secureResetUrl}?redirect_to=${encodeURIComponent(finalRedirectUrl)}`
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Add update password method
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signInWithGoogle,
    resetPassword,
    updatePassword,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}